import React, {
  ReactNode,
  useMemo,
  useState,
} from 'react';
import clsx from 'clsx';
import fromPairs from 'lodash/fromPairs';
import forIn from 'lodash/forIn';
import isNil from 'lodash/isNil';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import RemoveIcon from '@mui/icons-material/Remove';
import Accordion from 'core/components/Accordion';
import {
  useFormContext,
} from 'react-hook-form';
import {
  isEmptyPatientValue,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/CreatePatientDialog/helpers';
import OtherContactFields from 'pages/Dashboard/pages/Appointments/pages/List/components/CreatePatientDialog/OtherContactFields';
import PatientAddressFields from 'pages/Dashboard/pages/Appointments/pages/List/components/CreatePatientDialog/PatientAddressFields';
import PrimaryCarePhysicianInput from 'pages/Dashboard/components/PrimaryCarePhysicianInput';

const fieldsets = [
  {
    Component: PatientAddressFields,
    id: 'address',
    title: 'Address',
    defaultExpanded: true,
  },
  {
    Component: PrimaryCarePhysicianInput,
    id: 'primaryCarePhysician',
    title: 'Primary Care Physician',
    defaultExpanded: true,
  },
  {
    Component: OtherContactFields,
    id: 'guardian',
    title: 'Add Other Contact',
    defaultExpanded: false,
  },
];

type AccordionItemProps = {
  children: NonNullable<ReactNode> & ReactNode;
  expanded: boolean;
  onChange: (id: string) => void;
  isFilled?: boolean;
  headerText: string;
  id: string;
};

function FieldSet({
  children,
  expanded,
  onChange,
  headerText,
  isFilled = false,
  id,
}: AccordionItemProps) {
  const Icon = useMemo(() => {
    if (isFilled) {
      return CheckIcon;
    }
    return expanded ? RemoveIcon : AddIcon;
  }, [isFilled, expanded]);

  return (
    <Accordion
      header={<span className="font-semibold text-inherit">{headerText}</span>}
      expanded={expanded}
      onChange={() => { onChange(id); }}
      expandIcon={<Icon className={`nw-add-${id} text-base text-blue-500`} />}
      disableGutters
      headerClasses={{
        root: clsx(
          '!p-1.5 !min-h-[initial] hover:!text-blue-500', {
            '!bg-gray-100': expanded,
            '!text-gray-500': !expanded,
          },
        ),
        content: '!m-0',
        expandIconWrapper: isFilled ? '!rotate-0' : '',
      }}
      detailsClasses={{
        root: '!p-1.5',
      }}
      className="!my-2"
    >
      {children}
    </Accordion>
  );
}

function checkIfAnyFilled(item: Record<string, any>) {
  let hasValue = false;
  forIn(item, (prop: string, key: string) => {
    if (!isEmptyPatientValue(prop, key)) {
      hasValue = true;
    }
  });
  return hasValue;
}

export default function ExtraPatientFields() {
  const { watch, getFieldState, formState } = useFormContext();
  const [expandedSectionIds, setExpandedSectionIds] = useState(
    () => fieldsets
      .filter((fieldset) => fieldset.defaultExpanded)
      .map((fieldset) => fieldset.id),
  );
  const values = fieldsets.map(({ id }) => watch(id));
  const hasNoPrimaryCarePhysician = watch('hasNoPrimaryCarePhysician');
  const handleChange = (fieldsetId: string) => {
    setExpandedSectionIds((currentIds) => (
      currentIds.includes(fieldsetId)
        ? currentIds.filter((id) => id !== fieldsetId)
        : [...currentIds, fieldsetId]
    ));
  };

  const hasFilledValues = useMemo(() => (
    fromPairs(fieldsets.map(({ id }, index) => {
      const isPcpComplete = id === 'primaryCarePhysician' && hasNoPrimaryCarePhysician;
      const hasFilledValue = isPcpComplete || checkIfAnyFilled(values[index]);

      return [
        id,
        hasFilledValue && isNil(getFieldState(id, formState).error),
      ];
    }))
  ), [values, fieldsets, getFieldState, formState, hasNoPrimaryCarePhysician]);

  return (
    <div className="w-full">
      {fieldsets.map(({ Component, id, title }) => (
        <FieldSet
          key={id}
          expanded={expandedSectionIds.includes(id)}
          onChange={handleChange}
          headerText={title}
          isFilled={hasFilledValues[id]}
          id={id}
        >
          <Component />
        </FieldSet>
      ))}
    </div>
  );
}
